import { FC, useState } from "react";
import { Button, Card, CardBody, Flex, Text } from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/react";
import { PieChart as Chart, Pie } from "recharts";

import { AsyncTask, VerifiableCredential } from "../domain";
import { useWeb3ProviderContext } from "../contexts/Web3Provider";
import { generateProof } from "../utils/zk-proof";
import { sendZkProofTx } from "../adapters/ethereum";

interface ScoreProps {
  verifiableCredential: AsyncTask<VerifiableCredential, string>;
}

export const Score: FC<ScoreProps> = ({ verifiableCredential }) => {
  const { connectedProvider } = useWeb3ProviderContext();
  const [zkProofTx, setZkProofTx] = useState<AsyncTask<null, string>>({
    status: "pending",
  });

  const onMintLivenessProof = (verifiableCredential: VerifiableCredential) => {
    if (connectedProvider.status === "successful") {
      setZkProofTx({ status: "loading" });
      void generateProof(verifiableCredential)
        .then((zkProof) =>
          sendZkProofTx(connectedProvider.data, verifiableCredential, zkProof)
        )
        .then(() => setZkProofTx({ status: "successful", data: null }))
        .catch((err) => setZkProofTx({ status: "failed", error: err.message }));
    }
  };

  return (
    <Card width="400px">
      <CardBody>
        {verifiableCredential.status === "successful" ? (
          <Flex direction="column" align="center">
            <Text fontSize="3xl" marginBottom="6" marginTop="6">
              Liveness Score
            </Text>
            <Chart height={120} width={400}>
              <Pie
                dataKey="value"
                startAngle={180}
                endAngle={0}
                cx={200}
                cy={120}
                outerRadius={120}
                innerRadius={70}
                isAnimationActive={false}
                data={[
                  {
                    value: verifiableCredential.data.credentialSubject.score,
                    fill: "#11C200",
                  },
                  {
                    value:
                      100 - verifiableCredential.data.credentialSubject.score,
                    fill: "#EDF2F7",
                  },
                ]}
              />
            </Chart>
            {verifiableCredential.status === "successful" && (
              <Text fontSize="5xl">
                {verifiableCredential.data.credentialSubject.score}
              </Text>
            )}
            {verifiableCredential.data.credentialSubject.score > 0 ? (
              <Text
                fontSize="xl"
                textAlign="center"
                width="250px"
                marginTop="2"
              >
                Your liveness proof will be valid for 90 days.
              </Text>
            ) : (
              <Text
                fontSize="xl"
                textAlign="center"
                width="250px"
                marginTop="2"
              >
                At least 90 points are required to mint your proof.
              </Text>
            )}
            <Button
              size="lg"
              fontSize="lg"
              colorScheme="primary"
              marginTop="8"
              marginBottom="6"
              isDisabled={
                verifiableCredential.data.credentialSubject.score === 0
              }
              isLoading={zkProofTx.status === "loading"}
              onClick={() => onMintLivenessProof(verifiableCredential.data)}
            >
              Mint your liveness proof
            </Button>
          </Flex>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            height="100%"
          >
            <Spinner size="xl" />
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
